import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Button, Spinner } from './Button';
import { Callout } from './Feedback';

export function refreshLabel(
  updatedAt: number | null | undefined,
  now = Date.now(),
): string {
  if (!updatedAt) return '';
  const ago = Math.max(0, Math.round((now - updatedAt) / 1000));
  if (ago < 15) return 'Updated just now';
  if (ago < 60) return `Updated ${ago}s ago`;
  return `Updated ${Math.round(ago / 60)}m ago`;
}

/**
 * Quiet indicator for a list that polls: "Updated 12s ago", or "Updating…"
 * while a background refetch runs. Never a spinner over the list.
 */
export const RefreshStatus: React.FC<{
  updatedAt?: number | null;
  fetching?: boolean;
  className?: string;
}> = ({ updatedAt, fetching, className }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), 10_000);
    return () => window.clearInterval(t);
  }, []);
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-v2-11 text-v2-fg-4',
        className,
      )}
      aria-live="polite"
    >
      {fetching ? (
        <>
          <Spinner className="h-3 w-3" /> Updating…
        </>
      ) : (
        refreshLabel(updatedAt)
      )}
    </span>
  );
};

/** A background refetch failed while data is on screen: keep it, offer Retry. */
export const RefreshFailed: React.FC<{
  onRetry: () => void;
  className?: string;
  what?: string;
}> = ({ onRetry, className, what = 'This list' }) => (
  <Callout
    tone="warning"
    className={className}
    title={`${what} couldn’t refresh`}
  >
    Showing what was loaded last.
    <div className="mt-2">
      <Button size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  </Callout>
);
